import {
  getLockerverseAuctionMinimumBid,
  type LockerverseAuction,
  type LockerverseAuctionItem,
  type LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { ArrowRight } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import {
  formatAuctionBoundary,
  formatAuctionMoney as formatMoney,
  getAuctionDisplayStatus,
  isAuctionItemSoldOut,
} from "./auction-display.ts";
import {
  type AuctionSelection,
  getAuctionSelectionError,
  getAuctionSelectionPricing,
} from "./auction-payment.ts";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

const WHOLE_NUMBER = /^\d+$/;

type SelectionProps = {
  auction: LockerverseAuction;
  item: LockerverseAuctionItem;
  initialSelection: AuctionSelection | null;
  onContinue: (selection: AuctionSelection) => void;
  onError: (message: string) => void;
  onBack?: (() => void) | undefined;
};

function quantitySummary(value: string, amount: number) {
  if (
    !(WHOLE_NUMBER.test(value) && Number.isSafeInteger(Number(value) * amount))
  ) {
    return "Enter a whole-number quantity.";
  }
  return `Total: ${formatMoney(Number(value) * amount)}`;
}
function selectionInitialValue(
  buyNow: boolean,
  initial: AuctionSelection | null,
  minimum: number
) {
  if (buyNow) {
    return String(initial ? initial.quantity : 1);
  }
  return String(initial ? initial.amount / 100 : minimum);
}
function readOnlyTitle(
  item: LockerverseAuctionItem,
  status: LockerverseAuctionStatus
) {
  if (isAuctionItemSoldOut(item)) {
    return "This item is sold out.";
  }
  if (status === "draft") {
    return "This item is not open yet.";
  }
  if (status === "finished") {
    return item.saleMode === "buy_now"
      ? "Purchases are closed for this item."
      : "Bidding is closed for this item.";
  }
  return "This item is not available.";
}
function readOnlyDescription(
  auction: LockerverseAuction,
  item: LockerverseAuctionItem,
  status: LockerverseAuctionStatus
) {
  if (isAuctionItemSoldOut(item)) {
    return "All available quantities have been purchased.";
  }
  if (status === "draft") {
    return `This item opens ${formatAuctionBoundary(auction, "draft", item)}.`;
  }
  return item.saleMode === "buy_now"
    ? "This item is no longer available to purchase."
    : "Bidding has closed for this item.";
}
function selectionFromInput(
  auction: LockerverseAuction,
  buyNow: boolean,
  initialSelection: AuctionSelection | null,
  value: string
): AuctionSelection {
  return {
    amount: buyNow ? 0 : Number(value) * 100,
    includeServiceFee: initialSelection
      ? initialSelection.includeServiceFee
      : auction.serviceFeeEnabled && auction.serviceFeeDefaultChecked,
    quantity: buyNow ? Number(value) : 1,
  };
}
export function AuctionItemSelection({
  auction,
  item,
  initialSelection,
  onContinue,
  onError,
  onBack,
}: SelectionProps) {
  const buyNow = item.saleMode === "buy_now";
  const minimum = Math.ceil(getLockerverseAuctionMinimumBid(item) / 100);
  const [value, setValue] = useState(
    selectionInitialValue(buyNow, initialSelection, minimum)
  );
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  useAuctionBoundary(
    item.startAt ?? auction.startAt,
    item.endAt ?? auction.endAt
  );
  const status = getAuctionDisplayStatus(auction, item);
  const open =
    status === "active" &&
    !isAuctionItemSoldOut(item) &&
    item.saleMode !== "hybrid";
  function continueToPayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!WHOLE_NUMBER.test(value)) {
      setError(
        buyNow
          ? "Enter a whole-number quantity."
          : "Enter a whole-dollar bid amount."
      );
      return;
    }
    const selection = selectionFromInput(
      auction,
      buyNow,
      initialSelection,
      value
    );
    const message = getAuctionSelectionError(auction, item, selection);
    if (message) {
      setError(message);
      onError(message);
      return;
    }
    try {
      getAuctionSelectionPricing(auction, item, selection);
    } catch {
      setError("Enter a valid amount or quantity.");
      return;
    }
    onContinue(selection);
  }
  if (!open) {
    return (
      <div className="lockerverse-auctions__selection lockerverse-auctions__read-only">
        <p className="lockerverse-auctions__eyebrow">Item status</p>
        <h3>{readOnlyTitle(item, status)}</h3>
        <p>{readOnlyDescription(auction, item, status)}</p>
        {onBack ? (
          <button
            className="lockerverse-auctions__button"
            onClick={onBack}
            type="button"
          >
            Browse auction items <ArrowRight aria-hidden="true" size={16} />
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <form
      className="lockerverse-auctions__selection"
      onSubmit={continueToPayment}
    >
      <SelectionHeader buyNow={buyNow} item={item} minimum={minimum} />
      <div className="lockerverse-auctions__selection-body">
        <label className="lockerverse-auctions__field" htmlFor={id}>
          {buyNow ? "Quantity" : "Your bid amount"}
          <span className="lockerverse-auctions__amount-control">
            {buyNow ? null : <span aria-hidden="true">$</span>}
            <input
              aria-describedby={`${id}-help`}
              aria-invalid={Boolean(error)}
              autoComplete="off"
              id={id}
              inputMode="numeric"
              onChange={(event) => {
                setValue(event.target.value);
                setError(null);
              }}
              placeholder={buyNow ? "1" : String(minimum)}
              required
              value={value}
            />
          </span>
        </label>
        {error ? <p role="alert">{error}</p> : null}
        <p id={`${id}-help`}>
          {buyNow
            ? quantitySummary(value, item.buyNowPriceAmount ?? 0)
            : `Enter a whole-dollar bid of at least ${formatMoney(minimum * 100)}. Bid increment: ${formatMoney(item.bidIncrementAmount ?? 100)}.`}
        </p>
        <div className="lockerverse-auctions__selection-footer">
          <p>
            {buyNow
              ? "Continuing moves you into secure checkout where your payment is processed immediately."
              : "Continuing moves you into the secure payment step where your card is stored for settlement only if your bid wins."}
          </p>
          <button
            className="lockerverse-auctions__button"
            disabled={!auction.payment}
            type="submit"
          >
            {buyNow ? "Continue to Checkout" : "Continue to Payment"}{" "}
            <ArrowRight aria-hidden="true" size={16} />
          </button>
        </div>
        {auction.payment ? null : (
          <p role="alert">Payments are not configured for this auction.</p>
        )}
      </div>
    </form>
  );
}

function SelectionHeader({
  item,
  buyNow,
  minimum,
}: {
  item: LockerverseAuctionItem;
  buyNow: boolean;
  minimum: number;
}) {
  return (
    <div className="lockerverse-auctions__selection-header">
      <p className="lockerverse-auctions__eyebrow">Step 1 of 2</p>
      <h3>{buyNow ? "Buy this item now." : "Place your bid."}</h3>
      <p>
        {buyNow
          ? "Choose how many you want, then complete the secure checkout."
          : "Enter the bid amount you want to place for this item. You are only charged if this bid wins after bidding closes."}
      </p>
      <p>
        {buyNow ? "Price: " : "Minimum qualifying bid: "}
        <strong>
          {formatMoney(buyNow ? (item.buyNowPriceAmount ?? 0) : minimum * 100)}
        </strong>
      </p>
      {buyNow && item.availableQuantity !== null ? (
        <p>{item.availableQuantity} available</p>
      ) : null}
      {item.collectShippingAddress ? (
        <p>
          The next step will also collect a delivery address for seller
          fulfillment.
        </p>
      ) : null}
    </div>
  );
}
